import { useEffect, useMemo, useState } from "react";
import { Download, Loader2, RefreshCw, Search, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/lib/admin";

const PAGE_SIZE = 25;

function fmtNum(n) {
  return Number(n || 0).toLocaleString();
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function basisLabel(value) {
  if (value === "user_consented") return "User opted in";
  if (value === "admin_auto") return "Admin auto";
  return value || "—";
}

export default function AdminTrainingCorpusSection() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [basis, setBasis] = useState("all");
  const [role, setRole] = useState("all");
  const [days, setDays] = useState(90);
  const [page, setPage] = useState(0);
  const [openId, setOpenId] = useState("");

  const filters = useMemo(() => ({
    limit: PAGE_SIZE,
    skip: page * PAGE_SIZE,
    q: q.trim(),
    basis,
    role,
    days,
  }), [basis, days, page, q, role]);

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminApi.trainingCorpus(filters);
      setRows(data.rows || []);
      setSummary(data.summary || null);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error(err.message || "Training corpus failed to load.");
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [filters]);

  const exportCorpus = (format) => {
    window.location.href = adminApi.trainingCorpusExportUrl({
      format,
      q: q.trim(),
      basis,
      role,
      days,
    });
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-5" data-testid="admin-training-corpus-section">
      <div className="border border-[var(--accent)] p-4 bg-[var(--bg-glow)]" data-testid="training-corpus-policy-banner">
        <div className="flex items-start gap-3">
          <ShieldCheck size={16} className="text-[var(--accent)] mt-0.5 shrink-0" />
          <div>
            <p className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-[var(--accent)]">
              Consent-gated corpus
            </p>
            <p className="font-ui text-sm leading-relaxed text-[var(--text-muted)] mt-2">
              This view reads only the `training_corpus` collection. Regular-user chats enter this collection only after explicit training consent. Admin conversations appear because admin role is an internal training role. Memory-on alone does not place user chats here.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile testid="training-corpus-total" label="All corpus rows" value={fmtNum(summary?.all_time_total)} />
        <Tile testid="training-corpus-matching" label="Matching rows" value={fmtNum(summary?.matching_total)} />
        <Tile testid="training-corpus-user-consented" label="User opt-ins" value={fmtNum(summary?.user_consented)} />
        <Tile testid="training-corpus-admin-auto" label="Admin rows" value={fmtNum(summary?.admin_auto)} />
      </div>

      <div className="border border-[var(--border)] p-4" data-testid="training-corpus-filters">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <label className="md:col-span-2 flex flex-col gap-1">
            <span className="font-mono-ui text-[9px] tracking-[0.24em] uppercase text-[var(--text-dim)]">Search</span>
            <div className="relative">
              <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--text-dim)]" />
              <input
                value={q}
                onChange={(e) => { setQ(e.target.value); setPage(0); }}
                placeholder="email, user id, conversation id"
                data-testid="training-corpus-search"
                className="w-full pl-7 pr-3 py-2 bg-transparent border border-[var(--border)] font-mono-ui text-xs outline-none focus:border-[var(--accent)]"
              />
            </div>
          </label>
          <FilterSelect testid="training-corpus-basis" label="Basis" value={basis} onChange={(v) => { setBasis(v); setPage(0); }} options={[
            ["all", "All"],
            ["user_consented", "User opted in"],
            ["admin_auto", "Admin auto"],
          ]} />
          <FilterSelect testid="training-corpus-role" label="Role" value={role} onChange={(v) => { setRole(v); setPage(0); }} options={[
            ["all", "All"],
            ["user", "User"],
            ["admin", "Admin"],
          ]} />
          <FilterSelect testid="training-corpus-days" label="Window" value={String(days)} onChange={(v) => { setDays(Number(v)); setPage(0); }} options={[
            ["7", "7 days"],
            ["30", "30 days"],
            ["90", "90 days"],
            ["365", "1 year"],
            ["3650", "All"],
          ]} />
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
          <p className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-dim)]" data-testid="training-corpus-results-count">
            {loading ? "Loading…" : `${fmtNum(total)} consented conversation${total === 1 ? "" : "s"}`}
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={load}
              disabled={loading}
              data-testid="training-corpus-refresh"
              className="px-3 py-1.5 border border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--accent)] font-mono-ui text-[10px] tracking-[0.24em] uppercase inline-flex items-center gap-2 disabled:opacity-40 transition-colors"
            >
              {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
              Refresh
            </button>
            <button
              onClick={() => exportCorpus("jsonl")}
              disabled={total === 0}
              data-testid="training-corpus-export-jsonl"
              className="px-3 py-1.5 border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-[var(--bg)] font-mono-ui text-[10px] tracking-[0.24em] uppercase inline-flex items-center gap-2 disabled:opacity-40 transition-colors"
            >
              <Download size={12} /> JSONL
            </button>
            <button
              onClick={() => exportCorpus("csv")}
              disabled={total === 0}
              data-testid="training-corpus-export-csv"
              className="px-3 py-1.5 border border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--accent)] font-mono-ui text-[10px] tracking-[0.24em] uppercase inline-flex items-center gap-2 disabled:opacity-40 transition-colors"
            >
              <Download size={12} /> CSV
            </button>
          </div>
        </div>
      </div>

      <div className="space-y-3" data-testid="training-corpus-list">
        {loading && rows.length === 0 ? (
          <p className="py-8 text-center font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text-dim)]" data-testid="training-corpus-loading">
            <Loader2 size={14} className="inline animate-spin mr-2" /> Loading consented data…
          </p>
        ) : rows.length === 0 ? (
          <p className="border border-dashed border-[var(--border)] p-8 text-center font-ui text-sm text-[var(--text-muted)]" data-testid="training-corpus-empty">
            No consented training conversations match these filters.
          </p>
        ) : rows.map((row) => {
          const id = `${row.user_id}-${row.conversation_id}`;
          const open = openId === id;
          return (
            <article key={id} className="border border-[var(--border)]" data-testid={`training-corpus-row-${row.conversation_id}`}>
              <button
                type="button"
                onClick={() => setOpenId(open ? "" : id)}
                data-testid={`training-corpus-row-toggle-${row.conversation_id}`}
                className="w-full text-left p-4 hover:bg-[var(--bg-pill)] transition-colors"
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-ui text-sm text-[var(--text)] break-all">{row.email || row.user_id}</p>
                    <p className="font-mono-ui text-[9px] tracking-[0.2em] uppercase text-[var(--text-dim)] mt-1 break-all">
                      {row.conversation_id} · {fmtDate(row.updated_at)}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span data-testid={`training-corpus-basis-${row.conversation_id}`} className="font-mono-ui text-[9px] tracking-[0.18em] uppercase border border-[var(--accent)] text-[var(--accent)] px-2 py-1">
                      {basisLabel(row.consent_basis)}
                    </span>
                    <span data-testid={`training-corpus-count-${row.conversation_id}`} className="font-mono-ui text-[9px] tracking-[0.18em] uppercase border border-[var(--border)] text-[var(--text-dim)] px-2 py-1">
                      {row.message_count} messages
                    </span>
                  </div>
                </div>
              </button>
              {open && (
                <div className="border-t border-[var(--border)] p-4 space-y-3" data-testid={`training-corpus-preview-${row.conversation_id}`}>
                  {(row.messages_preview || []).map((m, idx) => (
                    <div key={`${row.conversation_id}-${idx}`} className="grid md:grid-cols-[110px_1fr] gap-2">
                      <div className="font-mono-ui text-[9px] tracking-[0.22em] uppercase text-[var(--text-dim)]">{m.role}</div>
                      <p className="font-ui text-sm leading-relaxed whitespace-pre-wrap text-[var(--text)]">{m.content || "—"}</p>
                    </div>
                  ))}
                  {row.message_count > (row.messages_preview || []).length && (
                    <p className="font-mono-ui text-[9px] tracking-[0.22em] uppercase text-[var(--text-dim)]" data-testid={`training-corpus-preview-truncated-${row.conversation_id}`}>
                      Preview only · export JSONL for full message set
                    </p>
                  )}
                </div>
              )}
            </article>
          );
        })}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text-dim)]" data-testid="training-corpus-pagination">
          <button
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            data-testid="training-corpus-page-prev"
            className="px-3 py-1.5 border border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors disabled:opacity-40"
          >
            ← Prev
          </button>
          <span data-testid="training-corpus-page-indicator">Page {page + 1} of {totalPages}</span>
          <button
            onClick={() => setPage((p) => (p + 1 < totalPages ? p + 1 : p))}
            disabled={page + 1 >= totalPages}
            data-testid="training-corpus-page-next"
            className="px-3 py-1.5 border border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors disabled:opacity-40"
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}

function Tile({ label, value, testid }) {
  return (
    <div className="border border-[var(--border)] px-4 py-3" data-testid={testid}>
      <div className="font-mono-ui text-[9px] tracking-[0.24em] uppercase text-[var(--text-dim)]">{label}</div>
      <div className="font-mono-ui text-xl text-[var(--text)] mt-1">{value}</div>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="font-mono-ui text-[9px] tracking-[0.24em] uppercase text-[var(--text-dim)]">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="bg-transparent border border-[var(--border)] px-2 py-2 font-mono-ui text-xs text-[var(--text)] focus:border-[var(--accent)] outline-none"
      >
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </label>
  );
}